import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { VoteCounter } from './VoteCounter';

export function LiveRoom({battleId,onLeave}:{battleId:string;onLeave:()=>void}) {
  const [session,setSession]=useState<any>(null);
  const [error,setError]=useState('');
  const [connected,setConnected]=useState(false);
  const [camera,setCamera]=useState(false);
  const [mic,setMic]=useState(false);
  const [busy,setBusy]=useState(false);
  const [participants,setParticipants]=useState<any[]>([]);
  const [chat,setChat]=useState<any[]>([]);
  const [message,setMessage]=useState('');
  const stage=useRef<HTMLDivElement>(null);
  const room=useRef<any>(null);
  useEffect(()=>{
    let stopped=false;let activeRoom:any;let timer:ReturnType<typeof setInterval>;
    const poll=async()=>{try{const d=await api.liveStatus(battleId);if(!stopped){setParticipants(d.participants);setChat(d.chat);}}catch(e:any){if(!stopped)setError(e.message);}};
    (async()=>{
      try {
        const s=await api.joinLive(battleId); if(stopped)return;setSession(s);await poll();timer=setInterval(poll,3000);
        if(!s.token || !s.url)return;
        const {Room,RoomEvent,Track}=await import('livekit-client'); if(stopped)return;
        activeRoom=new Room({adaptiveStream:true,dynacast:true});room.current=activeRoom;
        const attach=(track:any,participant:any)=>{
          if(stopped || !stage.current)return;
          const id=track.sid || `${participant.identity}-${track.kind}`;
          if(stage.current.querySelector(`[data-track="${id}"]`))return;
          const element=track.attach();element.autoplay=true;
          if(track.kind===Track.Kind.Video){
            element.playsInline=true;element.muted=participant===activeRoom.localParticipant;
            const card=document.createElement('div');card.className='live-media-tile';card.dataset.track=id;
            const label=document.createElement('span');label.textContent=participant.name || participant.identity;
            card.append(element,label);stage.current.append(card);
          }else{element.dataset.track=id;stage.current.append(element);}
        };
        const detach=(track:any)=>{track.detach().forEach((element:HTMLElement)=>{const parent=element.closest('.live-media-tile');if(parent)parent.remove();else element.remove();});};
        activeRoom.on(RoomEvent.TrackSubscribed,(track:any,_pub:any,p:any)=>attach(track,p));
        activeRoom.on(RoomEvent.TrackUnsubscribed,detach);
        activeRoom.on(RoomEvent.LocalTrackPublished,(pub:any)=>{if(pub.track?.kind===Track.Kind.Video)attach(pub.track,activeRoom.localParticipant);});
        activeRoom.on(RoomEvent.LocalTrackUnpublished,(pub:any)=>{if(pub.track)detach(pub.track);});
        activeRoom.on(RoomEvent.Disconnected,()=>{if(!stopped){setConnected(false);setCamera(false);setMic(false);setError('Video disconnected. Leave and rejoin to reconnect.');}});
        activeRoom.on(RoomEvent.MediaDevicesError,()=>setError('Camera or microphone access failed. Check your browser permissions and device.'));
        await activeRoom.connect(s.url,s.token);
        if(stopped){await activeRoom.disconnect();return;}
        setConnected(true);
      }catch(e:any){if(!stopped)setError(e.message || 'Could not join this room.');}
    })();
    return ()=>{stopped=true;clearInterval(timer);activeRoom?.disconnect();room.current=null;api.leaveLive(battleId).catch(()=>{});};
  },[battleId]);
  const toggle=async(kind:'camera'|'mic')=>{
    if(!room.current || busy)return;setBusy(true);setError('');
    try{if(kind==='camera'){await room.current.localParticipant.setCameraEnabled(!camera);setCamera(!camera);}else{await room.current.localParticipant.setMicrophoneEnabled(!mic);setMic(!mic);}}
    catch(e:any){setError(e.message || 'Could not access this device.');}finally{setBusy(false);}
  };
  const send=async()=>{if(!message.trim())return;setBusy(true);try{await api.sendLiveChat(battleId,message.trim());setMessage('');const d=await api.liveStatus(battleId);setChat(d.chat);}catch(e:any){setError(e.message);}finally{setBusy(false);}};
  return <div className="grid">
    <div className="card">
      <div className="flex-between wrap gap-8"><h2>Live room</h2><button className="btn btn-outline" onClick={onLeave}>Leave room</button></div>
      <p className="muted">{connected?'Video connected':session?.mode==='status-chat'?'Chat is available. Live video has not been configured for this site.':'Connecting…'} · {session?.role || 'Joining'} · {participants.length} in room</p>
      {error && <p role="alert" className="field-hint">{error}</p>}
      <div ref={stage} className="live-media-stage" aria-label="Live video and audio" />
      <div className="flex wrap gap-8" style={{marginTop:16}}>
        <button className="btn btn-primary" disabled={!connected || session?.role==='AUDIENCE' || busy} onClick={()=>toggle('camera')}>{camera?'Turn camera off':'Turn camera on'}</button>
        <button className="btn btn-primary" disabled={!connected || session?.role==='AUDIENCE' || busy} onClick={()=>toggle('mic')}>{mic?'Mute microphone':'Unmute microphone'}</button>
        <button className="btn btn-outline" disabled={!connected} onClick={()=>room.current?.startAudio().catch((e:any)=>setError(e.message))}>Enable sound</button>
      </div>
      {session?.role==='AUDIENCE' && <p className="muted">Audience members watch and listen. Assigned judges and finalists can broadcast.</p>}
    </div>
    <VoteCounter battleId={battleId}/>
    <div className="card"><h3>Live chat</h3><div className="chat-history" aria-live="polite">{chat.length?chat.map(m=><p key={m.id}><strong>{m.username}</strong> <span className="muted">{m.role}</span>: {m.body}</p>):<p className="muted">No messages yet.</p>}</div>
      <div className="flex gap-8"><input className="input" aria-label="Live chat message" placeholder="Message the room…" maxLength={2000} value={message} onChange={e=>setMessage(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')send();}}/><button className="btn btn-gold" disabled={busy || !session || !message.trim()} onClick={send}>Send</button></div>
    </div>
  </div>;
}
